/**
 * Dynamic Minimum Quantity Feature
 * Handles dynamic minimum quantities based on variant metafields
 */

class DynamicMinimumQuantity extends HTMLElement {
  constructor() {
    super();
    this.quantitySelector = null;
    this.quantityInput = null;
    this.minimumNotice = null;
    this.productForm = null;
    this.variantData = null;
  }

  connectedCallback() {
    this.quantitySelector = this.querySelector('.quantity-selector');
    this.quantityInput = this.querySelector('.quantity-selector__input');
    this.minimumNotice = this.querySelector('.quantity-minimum-notice');
    this.productForm = document.getElementById(this.quantityInput.getAttribute('form'));
    
    // Get variant data from the product form
    this.loadVariantData();
    
    // Listen for variant changes
    this.addEventListener('variant:change', this.handleVariantChange.bind(this));
    
    // Listen for form changes to detect variant selection
    if (this.productForm) {
      this.productForm.addEventListener('change', this.handleFormChange.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.productForm) {
      this.productForm.removeEventListener('change', this.handleFormChange.bind(this));
    }
  }

  loadVariantData() {
    // Try to get variant data from the product form or global product data
    const productDataScript = document.querySelector('script[type="application/json"][data-product-json]');
    if (productDataScript) {
      try {
        const productData = JSON.parse(productDataScript.textContent);
        this.variantData = productData.variants || [];
      } catch (e) {
        console.warn('Could not parse product variant data:', e);
      }
    }
  }

  handleFormChange(event) {
    // Check if this is a variant selection change
    if (event.target.name === 'id' || event.target.name.includes('option')) {
      setTimeout(() => {
        this.updateMinimumQuantity();
      }, 100); // Small delay to ensure variant data is updated
    }
  }

  handleVariantChange(event) {
    this.updateMinimumQuantity();
  }

  updateMinimumQuantity() {
    if (!this.quantityInput || !this.dataset.dynamicMinimum) {
      return;
    }

    const enableDynamic = this.dataset.dynamicMinimum === 'true';
    if (!enableDynamic) {
      return;
    }

    const metafieldKey = this.dataset.metafield;
    const fallbackMinimum = parseInt(this.dataset.fallbackMinimum) || 1;
    const showNotice = this.dataset.showNotice === 'true';

    if (!metafieldKey) {
      return;
    }

    // Get current selected variant
    const selectedVariantId = this.getSelectedVariantId();
    if (!selectedVariantId) {
      return;
    }

    // Find variant data
    const variant = this.findVariantById(selectedVariantId);
    if (!variant) {
      return;
    }

    // Get minimum quantity from metafield
    const metafieldParts = metafieldKey.split('.');
    const namespace = metafieldParts[0];
    const key = metafieldParts[1];
    
    let minimumQuantity = fallbackMinimum;
    
    if (variant.metafields && variant.metafields[namespace] && variant.metafields[namespace][key]) {
      const metafieldValue = variant.metafields[namespace][key];
      if (metafieldValue && metafieldValue > 0) {
        minimumQuantity = parseInt(metafieldValue);
      }
    }

    // Update the quantity input
    this.updateQuantityInput(minimumQuantity);
    
    // Update the notice
    this.updateMinimumNotice(minimumQuantity, showNotice);
  }

  getSelectedVariantId() {
    if (!this.productForm) {
      return null;
    }

    // Try to get variant ID from hidden input
    const variantInput = this.productForm.querySelector('input[name="id"]');
    if (variantInput && variantInput.value) {
      return parseInt(variantInput.value);
    }

    // Try to get from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) {
      return parseInt(variantId);
    }

    return null;
  }

  findVariantById(variantId) {
    if (!this.variantData || !Array.isArray(this.variantData)) {
      return null;
    }

    return this.variantData.find(variant => variant.id === variantId);
  }

  updateQuantityInput(minimumQuantity) {
    if (!this.quantityInput) {
      return;
    }

    const currentValue = parseInt(this.quantityInput.value) || 1;
    
    // Update min attribute
    this.quantityInput.setAttribute('min', minimumQuantity);
    
    // Update value if current value is below minimum
    if (currentValue < minimumQuantity) {
      this.quantityInput.value = minimumQuantity;
      this.quantityInput.size = Math.max(minimumQuantity.toString().length + 1, 2);
      
      // Trigger change event
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateMinimumNotice(minimumQuantity, showNotice) {
    if (!this.minimumNotice) {
      return;
    }

    if (showNotice && minimumQuantity > 1) {
      this.minimumNotice.textContent = `Minimum quantity: ${minimumQuantity} pieces`;
      this.minimumNotice.style.display = 'block';
    } else {
      this.minimumNotice.style.display = 'none';
    }
  }
}

// Register the custom element
if (!window.customElements.get('dynamic-minimum-quantity')) {
  window.customElements.define('dynamic-minimum-quantity', DynamicMinimumQuantity);
}

// Initialize dynamic minimum quantity on page load
document.addEventListener('DOMContentLoaded', function() {
  const quantitySelectors = document.querySelectorAll('.product-info__quantity-selector[data-dynamic-minimum="true"]');
  quantitySelectors.forEach(selector => {
    const wrapper = document.createElement('dynamic-minimum-quantity');
    wrapper.innerHTML = selector.innerHTML;
    selector.parentNode.replaceChild(wrapper, selector);
  });
});

// Also initialize when product info is re-rendered
document.addEventListener('product:rerender', function() {
  const quantitySelectors = document.querySelectorAll('.product-info__quantity-selector[data-dynamic-minimum="true"]');
  quantitySelectors.forEach(selector => {
    if (!selector.parentElement.classList.contains('dynamic-minimum-quantity')) {
      const wrapper = document.createElement('dynamic-minimum-quantity');
      wrapper.innerHTML = selector.innerHTML;
      selector.parentNode.replaceChild(wrapper, selector);
    }
  });
});
